import { LogOut, CoinsIcon } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useUser } from '@/contexts/UserContext';

interface DecodedToken {
  // biome-ignore lint/style/useNamingConvention: <explanation>
  realm_access?: {
    roles: string[];
  };
}

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = (props) => {
  const { children } = props;
  const auth = useAuth();
  const location = useLocation();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const { currentUser, isUserLoading } = useUser();

  useEffect(() => {
    const initializeUser = async () => {
      if (auth.isAuthenticated && auth.user?.access_token) {
        try {
          const decodedToken = jwtDecode(auth.user.access_token) as DecodedToken;
          const roles = decodedToken.realm_access?.roles || [];
          setUserRoles(roles);
        } catch (error) {
          console.error('Error decoding token:', error);
          setUserRoles([]);
        }
      }
    };

    initializeUser();
  }, [auth.isAuthenticated, auth.user]);

  const hasCreationAccess = userRoles.some((role) =>
    ['kapnetix-admin', 'kapnetix-kapture'].includes(role.toLowerCase())
  );
  const hasCleaningAccess = userRoles.some((role) =>
    ['kapnetix-admin', 'kapnetix-cleaning'].includes(role.toLowerCase())
  );

  return (
    <div className="flex w-full">
      <aside className="hidden md:flex w-[6.25rem] justify-center items-start py-4 bg-[#F2F3F5]">
        <Link to={'/'}>
          <img src={'/kapnetix-logo.svg'} alt={'Kapnetix'} />
        </Link>
      </aside>
      <div className="flex flex-col flex-1">
        <header className="w-full bg-white relative z-10 border-b border-[#D7DBE0]">
          <nav className="w-full flex">
            {hasCreationAccess && (
              <div className="nav-menu-item px-2 md:px-4 border-r uppercase">
                <Link to={'/capture'} className="hover:text-[#ee008d]" style={{ fontWeight: location.pathname === '/capture' ? 'bold' : 'normal' }}>
                  Capture
                </Link>
              </div>
            )}
            {hasCleaningAccess && (
              <div className="nav-menu-item px-2 md:px-4 border-r uppercase">
                <Link to={'/cleaning'} className="hover:text-[#ee008d]" style={{ fontWeight: location.pathname === '/cleaning' ? 'bold' : 'normal' }}>
                  Cleaning
                </Link>
              </div>
            )}
            <div className='ml-auto border-r' />
            {(
              !isUserLoading &&
              <div className="nav-menu-item px-2 md:px-4 border-r">
                <Link to={'/credits'} className="hover:text-[#ee008d]" >
                  {currentUser &&
                    <span>
                      <CoinsIcon size={18} className='inline align-text-bottom mb-[3px]' />
                      &nbsp;
                      {currentUser.credits}
                    </span>
                  }
                </Link>
              </div>
            )}
            <div className="nav-menu-item pl-2 md:pl-4 pr-2">
              <span>
                {auth.user?.profile.name}
              </span>
            </div>
            <div className="nav-menu-item pr-1 md:pr-2">
              <LogOut
                size={18}
                className={'inline align-text-bottom md:mb-[3px] cursor-pointer transition-all text-[#8F8F8F] hover:text-primary'}
                onClick={() => auth.signoutRedirect()}
              />
            </div>
          </nav>
        </header>
        <main className="mb-2 md:mb-8 py-4 md:py-10 px-4 md:px-8">
          <div className={'container'}>{children}</div>
        </main>
        <footer className="w-full mt-auto text-[0.725rem] text-center py-4 px-8 text-[#1b1531] opacity-30">App version: {import.meta.env.VITE_APP_VERSION}</footer>
      </div>
    </div>
  );
};
